import './styles/index.css'
import App from './components/App'
import AuthContext from './contexts/AuthContext'
import Keycloak from 'keycloak-js'
import React from 'react'
import { hydrate, render } from 'react-dom'
import introspectionQueryResultData from './fragmentTypes.json'
import registerServiceWorker from './registerServiceWorker'
import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { ApolloProvider } from '@apollo/react-components'
import { BrowserRouter } from 'react-router-dom'
import { InMemoryCache, IntrospectionFragmentMatcher, defaultDataIdFromObject } from 'apollo-cache-inmemory'
import { createHttpLink } from 'apollo-link-http'
import { persistCache } from 'apollo-cache-persist'
import { setContext } from 'apollo-link-context'
import { updateLocalStorage, KEYS } from './components/Auth'
// import { withClientState } from 'apollo-link-state'
import { resolvers, typeDefs } from './resolvers'
import ScrollToTop from './components/ScrollToTop'
import { onError } from 'apollo-link-error'
import { toast } from 'react-toastify'

require('dotenv').config()

const kc = Keycloak(`${process.env.PUBLIC_URL}/keycloak.json`)

const refreshToken = localStorage.getItem(KEYS.REFRESH_TOKEN)
const timeSkew = parseInt(localStorage.getItem(KEYS.TIMESKEW)) || 0
const token = localStorage.getItem(KEYS.ID_TOKEN)

const fragmentMatcher = new IntrospectionFragmentMatcher({ introspectionQueryResultData })
const cache = new InMemoryCache({
  fragmentMatcher,
  dataIdFromObject: object => {
    switch (object.__typename) {
    case 'ItemFile': return object.cuid ? object.cuid : object.pathOnDisk ? object.pathOnDisk : object.name
    default: return defaultDataIdFromObject(object)
    }
  }
})

persistCache({
  cache,
  storage: window.localStorage
})

; (async () => {
  let authenticated

  try {
    authenticated = await kc.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      token,
      refreshToken,
      timeSkew,
      promiseType: 'native',
    })
  } catch (err) {
    console.log('Failed to initialize keycloak')
  }

  if (!authenticated) kc.login()

  updateLocalStorage(kc)

  const authLink = setContext(async (_, { headers }) => {
    try {
      const refreshed = await kc.updateToken(120)
      if (refreshed) {
        updateLocalStorage(kc)
      }

      return {
        headers: {
          ...headers,
          authorization: kc.token ? `Bearer ${kc.token}` : '',
        }
      }

    } catch (err) {
      kc.login()
    }
  })

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        toast.dismiss()
        graphQLErrors.map(({ message, locations, path }) => {
          toast.error(`API Error: Message: ${message}, Path: ${path}`, { autoClose: false })
          console.log(
            `[GraphQL error]: Message: ${message}, Path: ${path}`,
          )
          return message
        })
      }
      if (networkError) {
        toast.dismiss()
        if (operation.operationName != 'UploadQuery') // UploadQuery operation is used by S3 file upload. do not show error message since S3 upload job is still in progress
          toast.error(`Network Error: Message: ${networkError.message}`, { autoClose: false })
        console.log(`[Network error]: ${networkError}`)
      }
    }
  )

  const httpLink = errorLink.concat(createHttpLink({
    uri: process.env.REACT_APP_GRAPHQL_SERVER
  }))

  const client = new ApolloClient({
    connectToDevTools: true,
    link: ApolloLink.from([authLink, /*stateLink,*/ httpLink]),
    cache: cache,
    typeDefs,
    resolvers
  })

  cache.writeData({
    data: {
      isLoggedIn: !!kc.token
    }
  })

  const rootElement = document.getElementById('root')

  const app = (
    <BrowserRouter getUserConfirmation={(message, callback) => {
      // this is the default behavior
      const allowTransition = window.confirm(message)
      callback(allowTransition)
    }} basename='/manager'>
      <ScrollToTop />
      <AuthContext.Provider value={kc}>
        <ApolloProvider client={client}>
          <App />
        </ApolloProvider>
      </AuthContext.Provider>
    </BrowserRouter>
  )

  if (rootElement.hasChildNodes()) {
    hydrate(app, rootElement)
  } else {
    render(app, rootElement)
  }
  registerServiceWorker()

})()
